/**
 * Push Notification Communications - Type Definitions
 * Types specific to push notification communication management
 */

import type { AppPushMessageModelView, AppPushNotificationModelView } from '@actschurch/ccms-sdk';
import type { AppPushTemplateModelView } from '@actschurch/ccms-sdk';

// ============================================================================
// SDK Model Interfaces (imported from SDK)
// ============================================================================

export type { AppPushMessageModelView, AppPushNotificationModelView, AppPushTemplateModelView };

// ============================================================================
// Push Notification Queue Item (with required ID for DataTable)
// ============================================================================

export type PushQueueRow = AppPushMessageModelView & {
  id: number; // pushMessageID mapped to id for DataTable compatibility
};

export type PushTemplateRow = AppPushTemplateModelView & {
  id: number; // Map template ID to id for DataTable compatibility
};

// ============================================================================
// Component Props Interfaces
// ============================================================================

export interface PushQueueTabProps {
  queueItems: AppPushMessageModelView[];
  loading?: boolean;
  onRefresh: () => void;
  onView: (item: AppPushMessageModelView) => void;
  onEdit: (item: AppPushMessageModelView) => void;
  onDelete: (id: number) => Promise<void>;
  /** Deletes the whole selection in a single request. */
  onBulkDelete: (ids: number[]) => Promise<unknown>;
  onRetry: (id: number) => Promise<void>;
  onUpdatePriority: (id: number, priority: number) => Promise<void>;
}

export interface PushTemplatesTabProps {
  templates: AppPushTemplateModelView[];
  loading?: boolean;
  onRefresh: () => void;
  onView: (template: AppPushTemplateModelView) => void;
  onEdit: (template: AppPushTemplateModelView) => void;
  onDelete: (id: number) => Promise<void>;
  onCreate: () => void;
}

export interface PushTestTabProps {
  templates: AppPushTemplateModelView[];
  loading?: boolean;
  onSend: (data: PushTestData) => Promise<void>;
}

export interface PushQueueModalProps {
  opened: boolean;
  onClose: () => void;
  item: AppPushMessageModelView | null;
  mode: 'view' | 'edit';
  onSave?: (data: Partial<AppPushMessageModelView>) => Promise<void>;
  loading?: boolean;
}

export interface PushTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  template: AppPushTemplateModelView | null;
  mode: 'view' | 'edit' | 'create';
  onSave?: (data: Partial<AppPushTemplateModelView>) => Promise<void>;
  loading?: boolean;
}

// ============================================================================
// Form Data Interfaces
// ============================================================================

/** A resolved push target (one device), built by the compose recipient picker. */
export interface PushRecipient {
  deviceToken: string;
  deviceID: string;
  appId?: string;
  platform?: string;
}

export interface PushTestData {
  useTemplate: boolean;
  templateId?: number;
  userId?: string;
  deviceToken?: string;
  deviceID?: string;
  /** Resolved recipients from the user-device picker. When present, one push is queued per entry. */
  recipients?: PushRecipient[];
  title: string;
  body: string;
  imageUrl?: string;
  sound?: string;
  badge?: string;
  data?: Record<string, string>; // Custom data for deep linking
  priority: number;
  scheduledDateTime?: Date;
  addToQueue: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function mapPushToRow(push: AppPushMessageModelView): PushQueueRow {
  return {
    ...push,
    id: push.messageID,
  };
}

export function mapTemplateToRow(template: AppPushTemplateModelView): PushTemplateRow {
  return {
    ...template,
    id: template.templateID,
  };
}

// ============================================================================
// Push Notification Helpers
// ============================================================================

export function getTargetType(item: AppPushMessageModelView): string {
  if (item.userId) return 'User ID';
  return 'Unknown';
}

export function getTargetValue(item: AppPushMessageModelView): string {
  return recipientName(item) || item.userEmail || item.deviceId || item.userId || 'N/A';
}

/**
 * A push template is a notification SPEC, not a message body:
 *   { "title": …, "body": …, "icon": …, "tag": …, "data": {…}, "actions": [ … ] }
 * (see _communication/<Module>/<Template>/push.json)
 */
export interface PushSpec {
  title?: string;
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  data?: Record<string, unknown>;
  actions?: { action: string; title: string }[];
}

/**
 * Parses a decoded template into its spec. Returns null for a legacy plain-text template.
 *
 * Selecting a template used to drop the raw JSON straight into the Message box, so the push went out
 * with the JSON as its text — and because a title+body were then present, the sender skipped rendering
 * altogether and shipped it verbatim.
 */
export function parsePushSpec(decoded?: string | null): PushSpec | null {
  if (!decoded) return null;
  try {
    const parsed = JSON.parse(decoded);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && (parsed.title || parsed.body)) {
      return parsed as PushSpec;
    }
  } catch {
    /* not JSON — a plain-text template */
  }
  return null;
}

/**
 * Encodes a push body for storage. Bodies are held base64-encoded, and btoa() is Latin-1 only — it
 * throws on any non-ASCII character (an accent or emoji in a template) — so the text is converted to
 * UTF-8 bytes first. Inverse of decodePushBody.
 */
export function encodePushBody(text: string): string {
  if (!text) return '';
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

/**
 * Parses the yyyyMMddHHmmss audit stamp the backend stores as a decimal (createdDateTime /
 * modifiedDateTime) into a Date. Returns undefined for anything that isn't a 14-digit stamp.
 */
export function parseAuditStamp(value?: number | null): Date | undefined {
  if (!value) return undefined;

  const s = String(value);
  if (s.length !== 14) return undefined;

  const d = new Date(
    Number(s.slice(0, 4)),
    Number(s.slice(4, 6)) - 1,
    Number(s.slice(6, 8)),
    Number(s.slice(8, 10)),
    Number(s.slice(10, 12)),
    Number(s.slice(12, 14)),
  );

  return Number.isNaN(d.getTime()) ? undefined : d;
}

/**
 * The recipient's display name, resolved server-side from the push's deviceId.
 * Empty when the device is no longer registered to anyone (app uninstalled, device removed).
 */
export function recipientName(item: AppPushMessageModelView): string {
  const full = (item as any).userFullName as string | undefined;
  if (full && full.trim()) return full.trim();

  const parts = [item.userFirstName, (item as any).userSurname as string | undefined]
    .filter((p) => !!p && String(p).trim())
    .map((p) => String(p).trim());

  return parts.join(' ');
}

/**
 * The delivery status to show for a row.
 *
 * `status` is what the sender worker recorded. Rows written before the API returned it fall back to
 * the server-derived `deliveryStatus`, and finally to a guess from isSent/errorMessage — otherwise
 * the badge renders blank and the Retry action (gated on "Failed") never appears.
 */
export function statusOf(item: AppPushMessageModelView): string {
  if (item.status) return item.status;
  if ((item as any).deliveryStatus) return (item as any).deliveryStatus as string;
  if (item.isSent) return 'Sent';
  if (item.errorMessage) return 'Failed';
  return 'Pending';
}

/**
 * Decodes a push body for display.
 *
 * Bodies are stored base64-encoded (AppPushMessage.Insert), but the worker writes the decoded body
 * back to the row when it sends, so a row can legitimately hold either form.
 *
 * Never decode twice. Plain ASCII is frequently itself valid base64, so a second atob() "succeeds"
 * and returns binary garbage — that was the mojibake in the outbox. We also re-interpret the bytes as
 * UTF-8, since atob() alone yields Latin-1 and mangles any accent or emoji.
 */
export function decodePushBody(value?: string | null): string {
  if (!value) return '';

  const trimmed = value.trim();

  // Not base64-shaped (spaces, punctuation, wrong length) → it is already the plain message.
  if (trimmed.length === 0 || trimmed.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(trimmed)) {
    return value;
  }

  try {
    const binary = atob(trimmed);
    const text = new TextDecoder('utf-8').decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));

    // A false positive — plain text that merely looked like base64 — decodes to control characters or
    // U+FFFD replacement chars. In that case the original string was already the message.
    // eslint-disable-next-line no-control-regex
    if (!text || /[\u0000-\u0008\u000E-\u001F\uFFFD]/.test(text)) return value;

    return text;
  } catch {
    return value;
  }
}
